import React, { useState } from 'react';
import { Button, Typography, Grow, Fade, Skeleton } from '@mui/material';
import { OnboardingEventKeys, OnboardingEvents } from '../events';
import { Theme } from '@mui/material/styles';
import Heading from './elements/heading';
import Stepper from './elements/stepper';
import { EventBus } from '@/shared/utils';
import { CreativeRadio } from '@/shared/ui';
import { Instrument } from '../types';
import { useOnboarding } from '../hooks/use-onboarding';

interface InstrumentSelectionProps {
  eventBus: EventBus<OnboardingEvents>;
  theme: Theme;
  instruments: Instrument[];
}

const InstrumentSelection = ({ eventBus, theme, instruments }: InstrumentSelectionProps) => {
  const { selectedInstrument, instrumentSelectionDoHandler } = useOnboarding(eventBus);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    instrumentSelectionDoHandler({ instrumentName: event.target.value });
    if (event.target.value === 'Chitarra')
      eventBus.emit(OnboardingEventKeys.instrument.child.view, { parentName: 'Chitarra' });
  };

  const handleContinue = () => {
    if (!selectedInstrument || !selectedInstrument.instrumentName) return;
    // Emettiamo l'evento di selezione padre
    eventBus.emit(OnboardingEventKeys.instrument.selection.do, {
      instrumentName: selectedInstrument.instrumentName,
    });
  };

  return (
    <>
      <Fade in={true}>
        <div>
          <Stepper activeStep={0} theme={theme} />
        </div>
      </Fade>

      <section className="flex flex-col pt-10 pb-12 gap-y-8 items-center max-w-[700px] m-auto text-center">
        <Fade in={true} style={{ transitionDuration: '700ms' }}>
          <div>
            <Heading
              title="Quale strumento ti interessa?"
              subtitle="Scegli il tuo strumento preferito. Ci aiuterà a capire quali contenuti mostrarti."
            />
          </div>
        </Fade>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-[600px] w-full">
          {instruments
            .filter((instrument) => !instrument.parentInstrument)
            .map((instrument) => {
              return (
                <Grow
                  key={instrument.instrumentName}
                  in={true}
                  style={{ transformOrigin: 'center center' }}
                >
                  <div>
                    <CreativeRadio
                      imageSrc={instrument.icon?.url || '/images/default-icon.png'}
                      altText={instrument.instrumentName || 'Default Instrument'}
                      name={instrument.instrumentName}
                      value={instrument.instrumentName}
                      checked={selectedInstrument.instrumentName === instrument.instrumentName}
                      onChange={handleRadioChange}
                    />
                  </div>
                </Grow>
              );
            })}
        </div>

        <Grow in={true} style={{ transformOrigin: 'center center' }}>
          <div>
            <Button
              size="large"
              variant="contained"
              color="primary"
              className="max-w-full w-[220px]"
              disabled={!selectedInstrument.instrumentName}
              onClick={handleContinue}
            >
              <Typography variant="button-l">Continua</Typography>
            </Button>
          </div>
        </Grow>
      </section>
    </>
  );
};

export default InstrumentSelection;

export const InstrumentSelectionSkeleton = () => {
  return (
    <div className="flex flex-col pt-10 pb-12 gap-y-8 items-center max-w-[700px] m-auto text-center">
      {/* Titolo e sottotitolo */}
      <div className="w-full">
        <Skeleton variant="text" height={40} width="70%" />
        <Skeleton variant="text" height={20} width="50%" style={{ margin: '10px auto' }} />
      </div>

      {/* Griglia degli strumenti */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-[600px] w-full">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton
            key={`skeleton-instrument-${index}`}
            variant="rectangular"
            height={100}
            width="100%"
            className="rounded-lg"
          />
        ))}
      </div>

      {/* Indicatore di progresso */}
      <div className="flex mx-auto mt-4 gap-2 justify-center w-[40px]">
        <Skeleton variant="circular" height={30} width={30} />
      </div>

      {/* Pulsanti */}
      <div className="flex flex-wrap gap-4 justify-center mt-6">
        <Skeleton variant="rectangular" height={50} width={120} />
        <Skeleton variant="rectangular" height={50} width={120} />
      </div>
    </div>
  );
};
